import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

/**
 * Thrown by lazyFeatureRoute() when a feature chunk cannot be loaded or evaluated.
 *
 * Production builds hash their file names, so a stale tab gets a webpack ChunkLoadError. dev/test
 * builds don't hash, so after a deploy the old runtime finds a file with the right name from the
 * *new* build and fails while evaluating it with an unrelated-looking TypeError instead. Wrapping
 * every lazy-load failure lets both cases be recognized the same way.
 */
export class LazyFeatureLoadError extends Error {
  constructor(readonly cause: unknown) {
    super(`Failed to load lazy feature: ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = 'LazyFeatureLoadError';
  }
}

/** True for errors caused by a lazy chunk that no longer matches (or can't be fetched for) this build. */
export function isStaleChunkError(error: unknown): boolean {
  if (error instanceof LazyFeatureLoadError) return true;
  const err = error as { name?: string; message?: string } | null;
  return err?.name === 'ChunkLoadError' || /Loading chunk [\w-]+ failed/.test(err?.message ?? '');
}

/** How the service performs the full page load — swapped out in tests. */
export const STALE_CHUNK_RELOAD = new InjectionToken<(url: string) => void>('STALE_CHUNK_RELOAD', {
  providedIn: 'root',
  factory: () => (url: string) => window.location.assign(url),
});

const LAST_RELOAD_KEY = 'staleChunkReloadAt';
/** A reload within this window means the fresh page failed too — stop instead of looping. */
const RELOAD_LOOP_WINDOW_MS = 10_000;

/**
 * Recovers tabs left open across a deploy.
 *
 * Entity features are lazy chunks. A tab still running the previous build asks the server for chunk
 * files that no longer exist (or no longer match), so the navigation fails and nothing happens on
 * screen. When that happens this does a full page load of the URL the user was going to, which
 * picks up the new index.html and the new chunks (index.html is served with max-age=0).
 */
@Injectable({ providedIn: 'root' })
export class StaleChunkRecoveryService implements OnDestroy {
  private subscription?: Subscription;

  constructor(
    private router: Router,
    @Inject(STALE_CHUNK_RELOAD) private reload: (url: string) => void,
  ) {}

  start(): void {
    this.subscription ??= this.router.events
      .pipe(filter((e): e is NavigationError => e instanceof NavigationError))
      .subscribe((event) => this.onNavigationError(event));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private onNavigationError(event: NavigationError): void {
    if (!isStaleChunkError(event.error)) return;
    // Offline: a reload would only swap the failed navigation for the browser's offline page.
    if (navigator.onLine === false) return;
    if (this.reloadedRecently()) {
      console.error('Lazy chunk still failing after a reload; not reloading again.', event.error);
      return;
    }
    // Without storage the loop guard can't work, and an endless reload is worse than a failed click.
    if (!this.rememberReload()) return;
    this.reload(event.url);
  }

  private reloadedRecently(): boolean {
    try {
      const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
      return !!last && Date.now() - last < RELOAD_LOOP_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private rememberReload(): boolean {
    try {
      sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
      return true;
    } catch {
      return false;
    }
  }
}
